/**
 * Lightweight Before / After compare (PRD.164, PRD.235).
 *
 * - Two slots (Before / After) with "Choose Existing" + "Upload New".
 * - Compare candidates include both uploaded photos (patient_files, kind='image')
 *   and saved drawings (encounter_visual_charts, chart_type='drawing'), per PRD.235.
 * - Drawings are rendered via the same Konva helpers used by Saved Drawings —
 *   layered base image + transparent strokes overlay when the base is on
 *   Firebase Storage (CORS), otherwise a single composited data URL.
 * - Mixed pairs are supported (photo↔photo, drawing↔drawing, photo↔drawing).
 * - Renders side-by-side / slider compare when both slots are filled.
 * - No merged image artifact is produced; this is presentation-only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qEncounterVisualCharts,
  qPatientById,
  qPatientContact,
  type EncounterVisualChartRow,
  type PatientContactRow,
  type PatientDetailRow,
} from '@helios/data-access';
import { uploadToStorage } from '../../../lib/upload-to-storage';
import { ShareComparisonModal } from '../../../components/ShareComparisonModal';
import { generateThumbnail } from './SavedDrawPreview';
import { ReactCompareSlider } from 'react-compare-slider';
import {
  AssetImage,
  assetToPicked,
  chartHasAnyRef,
  pickedFromChart,
  pickedKey,
  useCompareAssets,
  type CompareAsset,
  type Picked,
  type SourceKind,
} from './compare-asset-utils';

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  visitId: string;
  /** Open a specific charting record (e.g. from clicking a card in All). */
  initialChartId?: string | null;
}

type Side = 'before' | 'after';

// ─── Component ───

export function BeforeAfterCompare({ patientId, encounterId, visitId, initialChartId }: Props) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { assets, assetByKey } = useCompareAssets(patientId, encounterId, orgId);

  const [before, setBefore] = useState<Picked | null>(null);
  const [after, setAfter] = useState<Picked | null>(null);
  const [pickerSide, setPickerSide] = useState<Side | null>(null);

  // ─── Persistence: load before/after pair rows ───
  const { data: chartRows = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qEncounterVisualCharts(encounterId),
  );

  // User can switch between saved pairs via the strip.
  // '__new__' = explicitly starting a fresh comparison.
  const [selectedChartId, setSelectedChartId] = useState<string | '__new__' | null>(
    initialChartId ?? null,
  );

  const targetChart = useMemo(() => {
    if (selectedChartId === '__new__') return null;
    if (selectedChartId) {
      const match = chartRows.find((c) => c.id === selectedChartId);
      if (match) return match;
    }
    if (initialChartId) {
      const match = chartRows.find((c) => c.id === initialChartId);
      if (match) return match;
    }
    return chartRows[0] ?? null;
  }, [chartRows, selectedChartId, initialChartId]);

  const persistedChartIdRef = useRef<string | null>(null);
  const hydratedRef = useRef(false);
  const hydratedForChartRef = useRef<string | null>(null);
  const [touched, setTouched] = useState(false);
  type CompareMode = 'side-by-side' | 'slider';
  const [compareMode, setCompareMode] = useState<CompareMode>('slider');

  // Hydrate from the target chart.
  useEffect(() => {
    if (!targetChart) return;
    if (hydratedRef.current && hydratedForChartRef.current === targetChart.id) return;

    const beforePicked = pickedFromChart(targetChart, 'before');
    const afterPicked = pickedFromChart(targetChart, 'after');

    // Wait until the referenced assets are available locally. For drawings
    // this means the drawing chart row + (if image-based) the base file are
    // resolvable.
    const beforeKey = pickedKey(beforePicked);
    const afterKey = pickedKey(afterPicked);
    const beforeReady = !beforeKey || assetByKey.has(beforeKey);
    const afterReady = !afterKey || assetByKey.has(afterKey);
    if (!beforeReady || !afterReady) return;

    setBefore(beforePicked);
    setAfter(afterPicked);
    if (targetChart.label === 'side-by-side' || targetChart.label === 'slider') {
      setCompareMode(targetChart.label as CompareMode);
    }
    persistedChartIdRef.current = targetChart.id;
    hydratedRef.current = true;
    hydratedForChartRef.current = targetChart.id;
    setTouched(false);
  }, [targetChart, assetByKey]);

  const startNewComparison = () => {
    setBefore(null);
    setAfter(null);
    persistedChartIdRef.current = null;
    setSelectedChartId('__new__');
    hydratedRef.current = true;
    hydratedForChartRef.current = '__new__';
    setTouched(false);
  };

  const beforeAsset = before ? (assetByKey.get(pickedKey(before) ?? '') ?? null) : null;
  const afterAsset = after ? (assetByKey.get(pickedKey(after) ?? '') ?? null) : null;

  // ─── Share to WhatsApp (PRD.240) ───
  // Patient identity for the confirm dialog.
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));
  const { data: contactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId),
  );
  const sharePatientName = patientRows[0]?.full_name ?? 'this patient';
  const sharePatientPhone = contactRows[0]?.mobile_number
    ? (contactRows[0].mobile_country_code
        ? `+${contactRows[0].mobile_country_code.replace(/^\+/, '')} `
        : '') + contactRows[0].mobile_number
    : null;

  const [shareOpen, setShareOpen] = useState(false);
  const [shareBeforeUrl, setShareBeforeUrl] = useState<string | null>(null);
  const [shareAfterUrl, setShareAfterUrl] = useState<string | null>(null);
  const [resolvingShareUrls, setResolvingShareUrls] = useState(false);

  // Resolve a CompareAsset to a single shareable image URL. Photos return
  // their stored URL directly; drawings get composited (base + strokes)
  // through the existing generateThumbnail helper so annotations are
  // preserved in the WhatsApp output.
  const resolveAssetImageUrl = useCallback(
    async (asset: CompareAsset | null): Promise<string | null> => {
      if (!asset) return null;
      if (asset.source === 'photo') return asset.baseUrl;
      if (asset.source === 'drawing' && asset.drawingRecord) {
        try {
          return await generateThumbnail(asset.drawingRecord, asset.baseUrl);
        } catch (err) {
          console.warn('[BeforeAfter] failed to composite drawing for share', err);
          return asset.baseUrl;
        }
      }
      return asset.baseUrl;
    },
    [],
  );

  // Resolve share URLs when the modal opens; skip while closed to avoid
  // doing the Konva offscreen work pre-emptively.
  useEffect(() => {
    if (!shareOpen) return;
    let cancelled = false;
    setResolvingShareUrls(true);
    Promise.all([resolveAssetImageUrl(beforeAsset), resolveAssetImageUrl(afterAsset)])
      .then(([b, a]) => {
        if (cancelled) return;
        setShareBeforeUrl(b);
        setShareAfterUrl(a);
      })
      .finally(() => {
        if (!cancelled) setResolvingShareUrls(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shareOpen, beforeAsset, afterAsset, resolveAssetImageUrl]);

  // ─── Save state ───
  const beforeKeyStr = pickedKey(before);
  const afterKeyStr = pickedKey(after);
  const savedBeforeKey = targetChart ? pickedKey(pickedFromChart(targetChart, 'before')) : null;
  const savedAfterKey = targetChart ? pickedKey(pickedFromChart(targetChart, 'after')) : null;
  const isDirty = touched && (beforeKeyStr !== savedBeforeKey || afterKeyStr !== savedAfterKey);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!db || saving) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const existingId = persistedChartIdRef.current;
      const beforeSource: SourceKind = before?.source ?? 'photo';
      const afterSource: SourceKind = after?.source ?? 'photo';
      const beforeFileId = before?.source === 'photo' ? before.fileId : null;
      const afterFileId = after?.source === 'photo' ? after.fileId : null;
      const beforeDrawingChartId = before?.source === 'drawing' ? before.chartId : null;
      const afterDrawingChartId = after?.source === 'drawing' ? after.chartId : null;

      if (existingId) {
        await db.execute(
          `UPDATE encounter_visual_charts
             SET before_file_id = ?, after_file_id = ?,
                 before_source = ?, after_source = ?,
                 before_chart_id = ?, after_chart_id = ?,
                 label = ?, updated_at = ?
           WHERE id = ?`,
          [
            beforeFileId,
            afterFileId,
            beforeSource,
            afterSource,
            beforeDrawingChartId,
            afterDrawingChartId,
            compareMode,
            now,
            existingId,
          ],
        );
      } else if (beforeKeyStr || afterKeyStr) {
        const id = crypto.randomUUID();
        persistedChartIdRef.current = id;
        await db.execute(
          `INSERT INTO encounter_visual_charts
            (id, org_id, clinic_id, patient_id, encounter_id, visit_id, chart_type,
             before_file_id, after_file_id,
             before_source, after_source, before_chart_id, after_chart_id,
             label, created_by_user_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            encounterId,
            visitId || null,
            'before_after',
            beforeFileId,
            afterFileId,
            beforeSource,
            afterSource,
            beforeDrawingChartId,
            afterDrawingChartId,
            compareMode,
            null,
            now,
            now,
          ],
        );
      }
      setTouched(false);
    } catch (err) {
      console.error('[BeforeAfter] save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const assign = (side: Side, picked: Picked | null) => {
    setTouched(true);
    if (side === 'before') setBefore(picked);
    else setAfter(picked);
  };

  const assignFromAssetKey = (side: Side, key: string) => {
    const a = assetByKey.get(key);
    if (!a) return;
    assign(side, assetToPicked(a));
  };

  const swap = () => {
    setTouched(true);
    const b = before;
    setBefore(after);
    setAfter(b);
  };

  return (
    <div style={{ padding: 'var(--sp-5)' }}>
      {/* Saved pairs strip — browse all saved comparisons */}
      {chartRows.length > 0 && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: 'var(--text-muted)',
              marginBottom: 8,
            }}
          >
            Saved Comparisons
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            {chartRows.filter(chartHasAnyRef).map((chart) => {
              const isActive = targetChart?.id === chart.id;
              const beforeP = pickedFromChart(chart, 'before');
              const afterP = pickedFromChart(chart, 'after');
              const beforeKey = pickedKey(beforeP);
              const afterKey = pickedKey(afterP);
              const beforeA = beforeKey ? (assetByKey.get(beforeKey) ?? null) : null;
              const afterA = afterKey ? (assetByKey.get(afterKey) ?? null) : null;

              return (
                <button
                  key={chart.id}
                  onClick={() => {
                    setSelectedChartId(chart.id);
                    hydratedForChartRef.current = null;
                    hydratedRef.current = false;
                    setTouched(false);
                  }}
                  style={{
                    width: 80,
                    height: 80,
                    flexShrink: 0,
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    border: isActive ? '2px solid var(--color-primary)' : '1px solid var(--border)',
                    background: 'var(--bg-surface-2)',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    position: 'relative',
                  }}
                >
                  <MiniHalf asset={beforeA} />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: '50%',
                      width: 1,
                      background: 'rgba(255,255,255,0.6)',
                      zIndex: 1,
                    }}
                  />
                  <MiniHalf asset={afterA} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Header row with actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          Assign a Before and After image to compare.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={startNewComparison}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 10px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <NavIcon name="plus" /> New Comparison
          </button>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: isDirty ? 'var(--color-warning-text)' : 'var(--text-muted)',
              marginRight: 4,
            }}
          >
            {isDirty ? 'Unsaved changes' : persistedChartIdRef.current ? 'Saved' : ''}
          </span>
          {beforeAsset && afterAsset && (
            <button
              onClick={swap}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 10px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <NavIcon name="list" /> Swap
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 12px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${!isDirty || saving ? 'var(--border)' : 'var(--color-primary)'}`,
              background: !isDirty || saving ? 'var(--bg-surface)' : 'var(--color-primary)',
              color: !isDirty || saving ? 'var(--text-muted)' : '#fff',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              cursor: !isDirty || saving ? 'default' : 'pointer',
              opacity: !isDirty ? 0.5 : 1,
            }}
          >
            <NavIcon name="check" />
            {saving ? 'Saving…' : 'Save'}
          </button>
          {/* PRD.240 — Share to WhatsApp. Only enabled for a saved (clean)
              comparison so we never share unsaved/dirty editor state. */}
          {(() => {
            const shareDisabled = !persistedChartIdRef.current || isDirty || saving;
            return (
              <button
                onClick={() => setShareOpen(true)}
                disabled={shareDisabled}
                title={
                  shareDisabled
                    ? isDirty
                      ? 'Save your changes before sharing'
                      : 'Save a comparison before sharing'
                    : 'Share this comparison via WhatsApp'
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${shareDisabled ? 'var(--border)' : 'var(--color-primary)'}`,
                  background: 'var(--bg-surface)',
                  color: shareDisabled ? 'var(--text-muted)' : 'var(--color-primary)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  cursor: shareDisabled ? 'not-allowed' : 'pointer',
                  opacity: shareDisabled ? 0.55 : 1,
                }}
              >
                <NavIcon name="message-square" />
                Share to WhatsApp
              </button>
            );
          })()}
        </div>
      </div>

      {beforeAsset && afterAsset ? (
        compareMode === 'slider' ? (
          <div
            style={{
              marginBottom: 'var(--sp-2)',
              position: 'relative',
            }}
          >
            <div
              style={{
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                border: '1px solid var(--border)',
                aspectRatio: '16 / 10',
                background: 'var(--bg-surface-2)',
              }}
            >
              <ReactCompareSlider
                itemOne={<AssetCompareView asset={beforeAsset} alt="Before" />}
                itemTwo={<AssetCompareView asset={afterAsset} alt="After" />}
                style={{ height: '100%', width: '100%' }}
              />
            </div>
            <CornerActions
              side="left"
              label="Before"
              orgId={orgId}
              patientId={patientId}
              encounterId={encounterId}
              visitId={visitId}
              db={db}
              onAssign={(picked) => assign('before', picked)}
              onClear={() => assign('before', null)}
              onOpenPicker={() => setPickerSide('before')}
            />
            <CornerActions
              side="right"
              label="After"
              orgId={orgId}
              patientId={patientId}
              encounterId={encounterId}
              visitId={visitId}
              db={db}
              onAssign={(picked) => assign('after', picked)}
              onClear={() => assign('after', null)}
              onOpenPicker={() => setPickerSide('after')}
            />
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              marginBottom: 'var(--sp-2)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              border: '1px solid var(--border)',
              background: '#000',
            }}
          >
            <SideBySidePane label="BEFORE" asset={beforeAsset} />
            <SideBySidePane label="AFTER" asset={afterAsset} />
          </div>
        )
      ) : (
        <div
          style={{
            position: 'relative',
            marginBottom: 'var(--sp-2)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface-2)',
            }}
          >
            <EmptySidePane asset={beforeAsset} placeholder="Select a Before image" />
            <EmptySidePane asset={afterAsset} placeholder="Select an After image" />
          </div>
          <CornerActions
            side="left"
            label="Before"
            orgId={orgId}
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            db={db}
            onAssign={(picked) => assign('before', picked)}
            onClear={() => assign('before', null)}
            onOpenPicker={() => setPickerSide('before')}
          />
          <CornerActions
            side="right"
            label="After"
            orgId={orgId}
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            db={db}
            onAssign={(picked) => assign('after', picked)}
            onClear={() => assign('after', null)}
            onOpenPicker={() => setPickerSide('after')}
          />
        </div>
      )}

      {beforeAsset && afterAsset && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 0,
            marginTop: 'var(--sp-2)',
          }}
        >
          <button
            onClick={() => setCompareMode('side-by-side')}
            style={{
              padding: '6px 14px',
              fontSize: 'var(--font-size-xs)',
              fontWeight: compareMode === 'side-by-side' ? 600 : 500,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm) 0 0 var(--radius-sm)',
              background:
                compareMode === 'side-by-side' ? 'var(--color-primary)' : 'var(--bg-surface)',
              color: compareMode === 'side-by-side' ? '#fff' : 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <NavIcon name="layout-grid" /> Side by Side
          </button>
          <button
            onClick={() => setCompareMode('slider')}
            style={{
              padding: '6px 14px',
              fontSize: 'var(--font-size-xs)',
              fontWeight: compareMode === 'slider' ? 600 : 500,
              border: '1px solid var(--border)',
              borderLeft: 'none',
              borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
              background: compareMode === 'slider' ? 'var(--color-primary)' : 'var(--bg-surface)',
              color: compareMode === 'slider' ? '#fff' : 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <NavIcon name="list" /> Slider
          </button>
        </div>
      )}

      {pickerSide && (
        <ExistingAssetPicker
          assets={assets}
          excludeKey={pickerSide === 'before' ? pickedKey(after) : pickedKey(before)}
          onPick={(key) => {
            assignFromAssetKey(pickerSide, key);
            setPickerSide(null);
          }}
          onClose={() => setPickerSide(null)}
          side={pickerSide}
        />
      )}

      {/* PRD.240 — Share to WhatsApp modal. Rendered at component root so
          the offscreen capture container is unaffected by ancestor styles. */}
      {persistedChartIdRef.current && (
        <ShareComparisonModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          patientId={patientId}
          patientName={sharePatientName}
          patientWhatsAppNumber={sharePatientPhone}
          encounterId={encounterId}
          chartId={persistedChartIdRef.current}
          beforeImageUrl={resolvingShareUrls ? null : shareBeforeUrl}
          afterImageUrl={resolvingShareUrls ? null : shareAfterUrl}
        />
      )}
    </div>
  );
}

/** Slider-pane wrapper (sits inside ReactCompareSlider as itemOne/itemTwo). */
function AssetCompareView({ asset, alt }: { asset: CompareAsset; alt: string }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'var(--bg-surface-2)',
      }}
    >
      <AssetImage asset={asset} alt={alt} objectFit="contain" />
    </div>
  );
}

/** Side-by-side pane with label header. */
function SideBySidePane({ label, asset }: { label: string; asset: CompareAsset }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', background: '#000' }}>
      <div
        style={{
          padding: '8px 0',
          textAlign: 'center',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.7)',
          background: '#000',
        }}
      >
        {label}
        {asset.source === 'drawing' && (
          <span
            style={{
              marginLeft: 6,
              padding: '0 5px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(255,255,255,0.18)',
              fontSize: 8,
            }}
          >
            DRAWING
          </span>
        )}
      </div>
      <div style={{ width: '100%', aspectRatio: '3 / 4', position: 'relative' }}>
        <AssetImage asset={asset} alt={label} objectFit="cover" />
      </div>
    </div>
  );
}

/** Empty / partial state pane: either renders the asset or a placeholder. */
function EmptySidePane({
  asset,
  placeholder,
}: {
  asset: CompareAsset | null;
  placeholder: string;
}) {
  return (
    <div
      style={{
        aspectRatio: '3 / 4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {asset ? (
        <AssetImage asset={asset} alt={placeholder} objectFit="cover" />
      ) : (
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {placeholder}
        </span>
      )}
    </div>
  );
}

// ─── Existing asset picker (modal) ───

function ExistingAssetPicker({
  assets,
  excludeKey,
  onPick,
  onClose,
  side,
}: {
  assets: CompareAsset[];
  excludeKey: string | null;
  onPick: (key: string) => void;
  onClose: () => void;
  side: Side;
}) {
  const pickable = assets.filter((a) => a.key !== excludeKey);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 998,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 640,
          maxWidth: '92vw',
          maxHeight: '80vh',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
            Choose {side === 'before' ? 'Before' : 'After'} image
          </span>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-surface)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-4)' }}>
          {pickable.length === 0 ? (
            <div
              style={{
                padding: 'var(--sp-6)',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              No existing photos or drawings for this visit. Use "Upload new" instead.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: 8,
              }}
            >
              {pickable.map((a) => (
                <PickerThumb key={a.key} asset={a} onPick={() => onPick(a.key)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function PickerThumb({ asset, onPick }: { asset: CompareAsset; onPick: () => void }) {
  return (
    <button
      onClick={onPick}
      title={asset.caption}
      style={{
        padding: 0,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2)',
        cursor: 'pointer',
        overflow: 'hidden',
        aspectRatio: '1',
        position: 'relative',
      }}
    >
      <AssetImage
        asset={asset}
        alt={asset.caption}
        objectFit={asset.source === 'drawing' ? 'contain' : 'cover'}
      />
      {/* Source badge: only show for drawings to keep the photo grid clean
          (PRD §5.5, §10.2). */}
      {asset.source === 'drawing' && (
        <span
          style={{
            position: 'absolute',
            bottom: 4,
            left: 4,
            padding: '1px 5px',
            borderRadius: 'var(--radius-sm)',
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          Drawing
        </span>
      )}
    </button>
  );
}

// ─── Corner action bar for the slider ───

interface CornerActionsProps {
  side: 'left' | 'right';
  label: string;
  orgId: string;
  patientId: string;
  encounterId: string;
  visitId: string;
  db: ReturnType<typeof usePowerSyncDb>['db'];
  onAssign: (picked: Picked) => void;
  onClear: () => void;
  onOpenPicker: () => void;
}

function CornerActions({
  side,
  label,
  orgId,
  patientId,
  encounterId,
  visitId,
  db,
  onAssign,
  onClear,
  onOpenPicker,
}: CornerActionsProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !db) return;
    setUploading(true);
    try {
      const fileId = crypto.randomUUID();
      const { downloadUrl } = await uploadToStorage({ orgId, patientId, file, fileId });
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_files
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, kind, filename, url, tag, mime_type, description, starred, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          fileId,
          orgId,
          null,
          patientId,
          encounterId,
          visitId || null,
          'image',
          file.name,
          downloadUrl,
          'before_after',
          file.type || null,
          null,
          0,
          now,
          now,
        ],
      );
      onAssign({ source: 'photo', fileId, chartId: null });
    } catch (err) {
      console.error('[BeforeAfter] upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        [side]: 8,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 6px',
        borderRadius: 'var(--radius-sm)',
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <span
        style={{
          color: '#fff',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          padding: '0 4px',
        }}
      >
        {label}
      </span>
      <CornerBtn title="Choose existing" onClick={onOpenPicker}>
        <NavIcon name="folder-open" />
      </CornerBtn>
      <CornerBtn
        title={uploading ? 'Uploading…' : 'Upload new'}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        <NavIcon name="plus" />
      </CornerBtn>
      <CornerBtn title="Clear" onClick={onClear}>
        <NavIcon name="x" />
      </CornerBtn>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChosen}
        style={{ display: 'none' }}
      />
    </div>
  );
}

function CornerBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 22,
        height: 22,
        borderRadius: 'var(--radius-sm)',
        border: 'none',
        background: 'rgba(255,255,255,0.12)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        padding: 0,
      }}
    >
      {children}
    </button>
  );
}

/** Mini thumbnail used inside the Saved Comparisons strip. */
function MiniHalf({ asset }: { asset: CompareAsset | null }) {
  if (!asset) {
    return <div style={{ width: '100%', height: '100%', background: 'var(--bg-surface-2)' }} />;
  }
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <AssetImage asset={asset} alt="" objectFit="cover" />
    </div>
  );
}
